import React, { useCallback, useContext, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import ModalContext from '../../../../contexts/ModalContext'
import axios from '../../../../config/axios'
import cross from '../../../../assets/addStockModal/cross.svg'
import rectangle from '../../../../assets/fashion/TableFashionHeader/rectangle.svg'
import OrdenoLoading from '../../../../components/OrdenoLoading'
import AttachmentManager, {
  type ExistingAttachment,
} from '../../../../components/AttachmentManager'
import { downloadRemoteFile } from '../../../../utils/downloadRemoteFile'
import openSignedAttachment from '../../../../utils/openSignedAttachment'
import {
  Card,
  FormGrid,
  FormGroup,
  Label,
  InputText,
  Separator,
  Title,
  Summary,
  SummaryItem,
  SummaryLabel,
  SummaryValue,
  KitSummaryLabel,
  Button,
  ShortLabel,
  ShortLabelText,
  SeparatorImg,
} from '../RegisterFashionOrder/style'
import {
  ModalShell,
  HeaderRow,
  HeaderTitle,
  CloseBtn,
  ScrollBody,
  FooterRow,
  ProductDiv,
  ProductFieldsRow,
  GradeTable,
  GradeLine,
  GradeColorCell,
  GradeCell,
} from './style'
import {
  type ProductApiData,
  type FormViewState,
  formatCurrency,
  countColors,
  mapSteelOrderResponseToDetail,
  getTotalPieces,
  formatProductDiscountLabel,
  effectiveItemPrice,
  compareSizes,
} from '../fashionOrderDetailFromApi'

const ORDERS_URL = `${process.env.REACT_APP_GATEWAY_BASE_URL}/steel-orders`

const formatFileSize = (bytes: number): string => {
  if (!bytes) return '—'
  const k = 1024
  const sizes = ['Bytes', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return `${Math.round((bytes / Math.pow(k, i)) * 100) / 100} ${sizes[i]}`
}

const getSafeValue = (value: unknown): string => {
  if (value === null || value === undefined) return '-'
  if (typeof value === 'object' && value !== null && 'name' in value)
    return String((value as { name?: string }).name || '-')
  return String(value) || '-'
}

const ViewFashionOrderModal: React.FC = () => {
  const navigate = useNavigate()
  const {
    setModalActive,
    selectedFashionOrderId,
    setSelectedFashionOrderId,
  } = useContext(ModalContext)

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [formState, setFormState] = useState<FormViewState | null>(null)
  const [addedProducts, setAddedProducts] = useState<ProductApiData[]>([])
  const [kitCount, setKitCount] = useState(0)
  const [attachments, setAttachments] = useState<ExistingAttachment[]>([])

  useEffect(() => {
    if (!selectedFashionOrderId) {
      setFormState(null)
      setAddedProducts([])
      setAttachments([])
      setError(null)
      return
    }

    const load = async () => {
      setLoading(true)
      setError(null)
      try {
        const { data } = await axios.get(`${ORDERS_URL}/order/${selectedFashionOrderId}`)
        const mapped = mapSteelOrderResponseToDetail(data as Record<string, unknown>)
        setFormState(mapped.formState)
        setAddedProducts(mapped.addedProducts)
        setKitCount(mapped.kitCount)
        setAttachments(mapped.attachments)
      } catch (e) {
        console.error(e)
        setError('Não foi possível carregar o pedido.')
      } finally {
        setLoading(false)
      }
    }

    load()
  }, [selectedFashionOrderId])

  const handleClose = () => {
    setModalActive(false)
    setSelectedFashionOrderId(null)
  }

  const handleEdit = () => {
    if (selectedFashionOrderId) {
      navigate(`/panel/fashion/edit/${selectedFashionOrderId}`)
      handleClose()
    }
  }

  const handleViewAttachment = useCallback((a: ExistingAttachment) => {
    if (a.filePath) void openSignedAttachment(a.filePath)
  }, [])

  const handleDownloadAttachment = useCallback((a: ExistingAttachment) => {
    if (!a.filePath) return
    void downloadRemoteFile(a.filePath, a.originalName)
  }, [])

  const noopFileChange = useCallback((_e: React.ChangeEvent<HTMLInputElement>) => {}, [])
  const noopRemoveFile = useCallback((_i: number) => {}, [])

  const renderReadonly = (label: string, value: string) => (
    <FormGroup>
      <Label>{label}</Label>
      <InputText type="text" readOnly value={value} />
    </FormGroup>
  )

  const renderGrade = (product: ProductApiData) => {
    if (!product.items?.length) return null

    const tamanhos = Array.from(new Set(product.items.map((i) => i.size).filter(Boolean))).sort(compareSizes)
    const coresNoItem = Array.from(new Set(product.items.map((i) => i.colorId)))

    const nomePorCor: { [id: number]: string } = {}
    product.selectedColors?.forEach((id, idx) => { nomePorCor[id] = product.colors?.[idx] || `Cor #${id}` })
    coresNoItem.forEach((id) => { if (!nomePorCor[id]) nomePorCor[id] = `Cor #${id}` })

    return (
      <GradeTable>
        <GradeLine $head>
          <GradeColorCell>Grade / Cor</GradeColorCell>
          {tamanhos.map((t) => <GradeCell key={t} $head>{t}</GradeCell>)}
        </GradeLine>
        {coresNoItem.map((corId) => (
          <GradeLine key={corId}>
            <GradeColorCell title={nomePorCor[corId]}>{nomePorCor[corId]}</GradeColorCell>
            {tamanhos.map((t) => {
              const qtd = product.items
                .filter((i) => i.colorId === corId && i.size === t)
                .reduce((soma, i) => soma + Number(i.quantity || 0), 0)
              return <GradeCell key={t}>{qtd > 0 ? qtd : '—'}</GradeCell>
            })}
          </GradeLine>
        ))}
      </GradeTable>
    )
  }

  const renderProductReadonly = (product: ProductApiData, index: number) => {
    const totalPieces = getTotalPieces(product)
    const subtotal = effectiveItemPrice(product)

    const fields = [
      { label: 'Produto', value: product.fashionProduct?.name ?? '-' },
      {
        label: 'Família',
        value:
          product.fashionProduct?.group?.family?.name || product.family?.name || '-',
      },
      { label: 'Cores', value: String(countColors(product)) },
      {
        label: 'Preço unit.',
        value:
          typeof product.fashionProduct?.price === 'number'
            ? formatCurrency(product.fashionProduct.price)
            : '-',
      },
      { label: 'Qtd. peças', value: String(totalPieces) },
      { label: 'Desconto', value: formatProductDiscountLabel(product) },
      {
        label: 'Subtotal',
        value: subtotal > 0 ? formatCurrency(subtotal) : '-',
      },
    ]

    return (
      <Card key={`${product.fashionProductId}-${index}`}>
        <ProductDiv>
          <ProductFieldsRow>
            {fields.map(({ label, value }, i) => (
              <React.Fragment key={label}>
                {i > 0 && <SeparatorImg src={rectangle} alt="" />}
                <ShortLabel>
                  <ShortLabelText>{label}:</ShortLabelText>&nbsp;
                  {getSafeValue(value)}
                </ShortLabel>
              </React.Fragment>
            ))}
          </ProductFieldsRow>
          {renderGrade(product)}
        </ProductDiv>
      </Card>
    )
  }

  const standaloneProducts = addedProducts
    .map((p, i) => ({ product: p, index: i }))
    .filter(({ product }) => product.kitIndex === null || product.kitIndex === undefined)

  const kitProducts = (kitIdx: number) =>
    addedProducts
      .map((p, i) => ({ product: p, index: i }))
      .filter(({ product }) => product.kitIndex === kitIdx)

  if (!selectedFashionOrderId) {
    return (
      <ModalShell onClick={(e) => e.stopPropagation()}>
        <HeaderRow>
          <HeaderTitle>Visualizar pedido</HeaderTitle>
          <CloseBtn type="button" onClick={handleClose} aria-label="Fechar">
            <img src={cross} alt="" width={14} height={14} />
          </CloseBtn>
        </HeaderRow>
        <p style={{ fontFamily: 'Inter, sans-serif', color: '#696969' }}>Nenhum pedido selecionado.</p>
      </ModalShell>
    )
  }

  return (
    <ModalShell onClick={(e) => e.stopPropagation()}>
      <HeaderRow>
        <HeaderTitle>Pedido fashion #{selectedFashionOrderId}</HeaderTitle>
        <CloseBtn type="button" onClick={handleClose} aria-label="Fechar">
          <img src={cross} alt="" width={14} height={14} />
        </CloseBtn>
      </HeaderRow>

      <ScrollBody>
        {loading && <OrdenoLoading variant='inline' text='Carregando dados do pedido...' style={{ padding: '2rem 0' }} />}
        {error && <p style={{ color: '#c00', fontFamily: 'Inter, sans-serif' }}>{error}</p>}

        {!loading && !error && formState && (
          <>
            <Card>
              <Title style={{ fontSize: 16, marginBottom: 12 }}>Participantes</Title>
              <FormGrid>
                {renderReadonly('Fornecedor', formState.supplier.companyName || '—')}
                {renderReadonly('Vendedor', formState.seller.name || '—')}
                {renderReadonly('Cliente', formState.company.companyName || '—')}
              </FormGrid>
              <Separator />
              <Title style={{ fontSize: 16, marginBottom: 12 }}>Detalhes do pedido</Title>
              <FormGrid>
                {renderReadonly('Tipo de pedido', formState.orderType || '—')}
                {renderReadonly('Data início da entrega', formState.startDateOfDelivery || '—')}
                {renderReadonly('Data final da entrega', formState.finalDateOfDelivery || '—')}
              </FormGrid>
              <FormGrid>
                {renderReadonly('Condição de pagamento', formState.paymentCondition || '—')}
                {renderReadonly('Tipo de frete', formState.shippingType || '—')}
                {renderReadonly('Valor do frete', formState.shippingCost || '—')}
              </FormGrid>
              <FormGrid>
                {renderReadonly('Código de rastreio', formState.trackingCode || '—')}
                <FormGroup>
                  <Label>Anexos</Label>
                  <AttachmentManager
                    readOnly
                    selectedFiles={[]}
                    existingAttachments={attachments}
                    onFileChange={noopFileChange}
                    onRemoveFile={noopRemoveFile}
                    onViewAttachment={handleViewAttachment}
                    onDownloadAttachment={handleDownloadAttachment}
                    formatFileSize={formatFileSize}
                  />
                </FormGroup>
                {renderReadonly(
                  'Valor máx. faturamento',
                  formState.maximumBillingAmount || '—',
                )}
              </FormGrid>
              <FormGrid>
                <FormGroup>
                  <Label>Informações adicionais</Label>
                  <InputText type="text" readOnly value={formState.extraInformation || '—'} />
                </FormGroup>
              </FormGrid>
              <Separator />
              <Summary>
                <SummaryItem>
                  <SummaryLabel>Conjuntos:</SummaryLabel>
                  <SummaryValue>{kitCount}</SummaryValue>
                </SummaryItem>
                <SummaryItem>
                  <SummaryLabel>Itens:</SummaryLabel>
                  <SummaryValue>{formState.quantityItems}</SummaryValue>
                </SummaryItem>
                <SummaryItem>
                  <SummaryLabel>Peças:</SummaryLabel>
                  <SummaryValue>{formState.quantityPieces}</SummaryValue>
                </SummaryItem>
                <SummaryItem>
                  <SummaryLabel>Total Cadastrado:</SummaryLabel>
                  <SummaryValue>{formState.totalCost}</SummaryValue>
                </SummaryItem>
                {formState.sellingPriceLabel && (
                  <SummaryItem>
                    <SummaryLabel>Total de Venda:</SummaryLabel>
                    <SummaryValue>{formState.sellingPriceLabel}</SummaryValue>
                  </SummaryItem>
                )}
              </Summary>
            </Card>

            {standaloneProducts.map(({ product, index }) => renderProductReadonly(product, index))}

            {Array.from({ length: kitCount }, (_, kitIdx) => {
              const kits = kitProducts(kitIdx)
              const kitTotalPieces = kits.reduce((sum, { product }) => {
                const pieces = getTotalPieces(product)
                return sum + pieces
              }, 0)
              const kitTotal = kits.reduce((sum, { product }) => sum + (product.subtotal || 0), 0)
              return (
                <React.Fragment key={`kit-${kitIdx}`}>
                  <Card>
                    <Summary>
                      <SummaryItem>
                        <KitSummaryLabel>Conjunto {kitIdx + 1}</KitSummaryLabel>
                      </SummaryItem>
                      <SummaryItem>
                        <SummaryLabel>Itens:</SummaryLabel>
                        <SummaryValue>{kits.length}</SummaryValue>
                      </SummaryItem>
                      <SummaryItem>
                        <SummaryLabel>Peças:</SummaryLabel>
                        <SummaryValue>{kitTotalPieces}</SummaryValue>
                      </SummaryItem>
                      <SummaryItem>
                        <SummaryLabel>Total:</SummaryLabel>
                        <SummaryValue>{formatCurrency(kitTotal)}</SummaryValue>
                      </SummaryItem>
                    </Summary>
                  </Card>
                  {kits.map(({ product, index }) => renderProductReadonly(product, index))}
                </React.Fragment>
              )
            })}
          </>
        )}
      </ScrollBody>

      <FooterRow>
        <Button type="button" variant="outline" onClick={handleClose}>
          Fechar
        </Button>
        <Button type="button" variant="primary" onClick={handleEdit}>
          Editar pedido
        </Button>
      </FooterRow>
    </ModalShell>
  )
}

export default ViewFashionOrderModal
